
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { TerraceSubmission } from '@/types/terrace';
import useAuth from '@/hooks/useAuth';
import { toast } from 'sonner';

export const useMySubmissions = (statusFilter: 'all' | 'pending' | 'approved' | 'rejected') => {
  const [submissions, setSubmissions] = useState<TerraceSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      toast.error("Please log in to view your submissions");
      navigate("/login");
      return;
    }

    fetchMySubmissions();
  }, [user, statusFilter, navigate]);

  const fetchMySubmissions = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let query = supabase
        .from("terrace_submissions")
        .select('*')
        .eq('submitted_by', user.id);
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.order('submitted_at', { ascending: false });

      if (error) {
        console.error("Error fetching submissions:", error);
        toast.error("Failed to fetch your submissions");
      } else if (data) {
        const transformedData: TerraceSubmission[] = data.map(submission => ({
          id: submission.id,
          name: submission.name,
          address: submission.address,
          neighborhood: submission.neighborhood,
          lat: submission.lat,
          lng: submission.lng,
          imageUrl: submission.imageurl,
          sunStatus: submission.sunstatus as 'sunny' | 'soon' | 'not-sunny',
          openingHours: submission.openinghours,
          orientation: submission.orientation,
          hasFood: submission.hasfood,
          petFriendly: submission.petfriendly,
          servesAlcohol: submission.servesalcohol,
          terraceDescription: submission.terracedescription,
          website: submission.website,
          phone: submission.phone,
          type: submission.type,
          status: submission.status as 'pending' | 'approved' | 'rejected',
          submitted_by: submission.submitted_by,
          submitted_at: submission.submitted_at
        }));
        
        setSubmissions(transformedData);
      }
    } catch (error) {
      console.error("Fetch submissions error:", error);
      toast.error("An error occurred while fetching your submissions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status: 'pending' | 'approved' | 'rejected') => {
    return submissions.filter(s => s.status === status).length;
  };

  return {
    submissions,
    loading,
    getStatusCount
  };
};
